/**
 * 旧「表形式」のひな形を表セクションへ移行できるかを変換前に診断し、移行不可の理由を返す。
 * 検証対象は以下の3点。
 *
 * - 旧「表形式」であること
 * - 各ページで、表の列になる質問が1つ以上あること
 * - 各質問の配下で、同じ経路上にある条件分岐が3階層以下であること
 */
import {
  ResponseTypeEnum,
  TemplateLayoutTypeEnum,
  TemplateNodeTypeEnum,
  type TemplateNode,
  type TemplatePage,
} from '@ulysses-inc/harami_api_client'
import type { EligibilityResult, MigrationSource } from './types'
import { flattenNodes } from './uuidMapping'

// 表セクションでは、条件分岐の階層を3階層までに制限している。
// API 側の表セクション検証（maxTableLogicDepth=3）と対応する。
// そのため、同じ経路に条件分岐が4つ以上あるひな形は移行対象外にする。
const MAX_TABLE_LOGIC_DEPTH = 3

const exceedsMaxLogicDepthOnAPath = (question: TemplateNode): boolean => {
  const visit = (
    nodes: TemplateNode[] | undefined,
    logicCount: number,
  ): boolean =>
    (nodes ?? []).some(node => {
      const nextCount =
        logicCount + (node.type === TemplateNodeTypeEnum.Logic ? 1 : 0)
      return nextCount > MAX_TABLE_LOGIC_DEPTH || visit(node.nodes, nextCount)
    })
  return visit(question.nodes, 0)
}

// 移行後は各ページのルートセクション直下の質問が表の列になる。
// 取り込み項目は変換時に除外されるため、それ以外の質問がないページは列が0件になり、
// API 側の表セクション検証（列が1つ以上必要）で失敗する。
const hasTableColumn = (page: TemplatePage): boolean =>
  (page.nodes?.[0]?.nodes ?? []).some(
    node =>
      node.type === TemplateNodeTypeEnum.Question &&
      node.question?.responseType !== ResponseTypeEnum.GRID_VARIABLE,
  )

export const diagnoseEligibility = (
  source: MigrationSource,
): EligibilityResult => {
  const { template, pagesWithRule } = source
  if (template.layoutType !== TemplateLayoutTypeEnum.Grid) {
    return {
      eligible: false,
      reasons: ['旧「表形式」のひな形ではありません。'],
    }
  }

  const reasons: string[] = []
  const pagesWithoutColumn = pagesWithRule.pages.filter(
    page => !hasTableColumn(page),
  )
  if (pagesWithoutColumn.length > 0) {
    const pageNames = pagesWithoutColumn
      .map(page => `「${page.name}」`)
      .join('、')
    reasons.push(
      `取り込み項目のみで構成されたページ（${pageNames}）があるため、移行できません。`,
    )
  }

  const nodes = flattenNodes(pagesWithRule.pages)
  const questions = nodes.filter(
    node => node.type === TemplateNodeTypeEnum.Question,
  )

  if (questions.some(exceedsMaxLogicDepthOnAPath)) {
    reasons.push(
      `同じ経路上の条件分岐が${MAX_TABLE_LOGIC_DEPTH}階層を超えています。`,
    )
  }
  return reasons.length === 0
    ? { eligible: true }
    : { eligible: false, reasons }
}
